import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface ClientUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email?: string;
  role: string;
  is_active: boolean;
  has_2fa_enabled: boolean;
  two_factor_verified_at: string | null;
  totp_bypass_until: string | null;
  totp_bypass_reason: string | null;
}

export interface Disable2FAResult {
  success: boolean;
  error_message?: string;
}

export interface GrantBypassResult {
  success: boolean;
  bypass_until: string;
  error_message?: string;
}

// Fetch all users for a client organization with their 2FA status
// Uses edge function to query the client's database
export function useClientUsers(clientId: string) {
  return useQuery({
    queryKey: ['client-users', clientId],
    queryFn: async (): Promise<ClientUser[]> => {
      if (!clientId) return [];

      // Edge functions.invoke doesn't support query params directly, use fetch
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/get-client-users?client_id=${clientId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${supabaseAnonKey}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch client users');
      }

      const result = await response.json();
      return result.users || [];
    },
    enabled: !!clientId,
  });
}

// Superadmin: Permanently disable 2FA for a user
export function useSuperadminDisable2FA() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      targetUserId,
      reason,
    }: {
      targetUserId: string;
      reason: string;
    }): Promise<Disable2FAResult> => {
      const { data, error } = await supabase.rpc('superadmin_disable_2fa' as never, {
        p_target_user_id: targetUserId,
        p_reason: reason,
      } as never);

      if (error) throw error;

      const result = (data as any)?.[0];
      if (!result?.success) {
        throw new Error(result?.error_message || 'Failed to disable 2FA');
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-users'] });
    },
  });
}

// Superadmin: Grant temporary 2FA bypass
export function useSuperadminGrant2FABypass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      targetUserId,
      hours,
      reason,
    }: {
      targetUserId: string;
      hours: number;
      reason: string;
    }): Promise<GrantBypassResult> => {
      const { data, error } = await supabase.rpc('superadmin_grant_2fa_bypass' as never, {
        p_target_user_id: targetUserId,
        p_hours: hours,
        p_reason: reason,
      } as never);

      if (error) throw error;

      const result = (data as any)?.[0];
      if (!result?.success) {
        throw new Error(result?.error_message || 'Failed to grant bypass');
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-users'] });
    },
  });
}

// Superadmin: Revoke 2FA bypass early
export function useSuperadminRevoke2FABypass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (targetUserId: string): Promise<{ success: boolean }> => {
      const { error } = await supabase
        .from('user_two_factor')
        .update({
          totp_bypass_until: null,
          totp_bypass_reason: null,
        } as never)
        .eq('user_id', targetUserId as never);

      if (error) throw error;

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-users'] });
    },
  });
}
